import * as React from 'react';
import { useSelector } from 'react-redux';

import UserImage from '../../../components/UserImage/index';
import { selectUserById } from '../../../common/slice/userList';
import { selectOrgById } from '../../../common/slice/orgList';

/**
 * Actor rendering for the Combined View timeline.
 *
 * Entries carry bare user and org ids. The records behind them come from the
 * shared `userList` / `orgList` stores, which the saga hydrates from the ids
 * each page reports. Two consequences shape this file:
 *
 * 1. A record may not have arrived yet - hydration lands a tick after the
 *    entries do. Every accessor therefore degrades to a placeholder instead of
 *    throwing, so a page renders immediately and fills in names as they resolve.
 * 2. An id may never resolve at all: a user deleted years ago, or one the
 *    caller has no visibility of. That is also a placeholder, not an error.
 */

/** Shown in place of a name that has not resolved (or never will). */
const UNKNOWN_USER = "Unknown user";
const UNKNOWN_ORG = "Unknown organization";

/**
 * Reads a user record from the shared store. Returns a consistent shape whether
 * or not the record is present, so callers never branch on undefined.
 */
export function useActor(userId) {
    const record = useSelector(selectUserById(userId));
    if (!userId) {
        return { id: 0, name: "", imageUrl: "", isActive: true, resolved: false };
    }
    if (!record) {
        return { id: userId, name: "", imageUrl: "", isActive: true, resolved: false };
    }
    const name = `${record.first_name || ''} ${record.last_name || ''}`.trim() || record.user_name || "";
    return {
        id: userId,
        name: name,
        imageUrl: record.profile_image || "",
        // Absent means active: the field is only present on records the user
        // service marks, and treating "not yet known" as inactive would flag
        // every actor as inactive for the first render.
        isActive: record.is_active !== false,
        resolved: true
    };
}

export function useOrg(orgId) {
    const record = useSelector(selectOrgById(orgId));
    if (!orgId) {
        return { id: 0, name: "", imageUrl: "", resolved: false };
    }
    if (!record) {
        return { id: orgId, name: "", imageUrl: "", resolved: false };
    }
    return { id: orgId, name: record.name || "", imageUrl: record.profile_image || "", resolved: true };
}

/**
 * The actor's avatar. Renders the shared UserImage, which already falls back to
 * initials or a default when it has no URL.
 */
export function ActorAvatar({ userId, className = "profilePic" }) {
    const actor = useActor(userId);
    return <UserImage profileUrl={actor.imageUrl} className={className} profileName={actor.name} />;
}

/**
 * The actor's display name, with the inactive marker the timeline shows beside
 * users who have since been deactivated.
 */
export function ActorName({ userId, inner }) {
    const actor = useActor(userId);
    if (!userId) {
        return null;
    }
    return (
        <a className="user_name tool_tip" inner={inner} userid={userId}>
            {actor.resolved ? actor.name : UNKNOWN_USER}
            {actor.resolved && !actor.isActive && (<span className="gray ml"> (Inactive)</span>)}
        </a>
    );
}

/** An organization's display name, used by activity assignee transfers. */
export function OrgName({ orgId }) {
    const org = useOrg(orgId);
    if (!orgId) {
        return null;
    }
    return (
        <a className="org_name org_tool_tip" orgid={orgId}>
            {org.resolved ? org.name : UNKNOWN_ORG}
        </a>
    );
}
function isRealId(id) {
    return !!id && id !== "0";
}

/**
 * Renders whichever side of a transfer is populated: an activity can move
 * between users, between organizations, or both.
 */
export function TransferParty({ userId, orgId }) {
    if (isRealId(userId)) {
        return <ActorName userId={userId} />;
    }
    if (isRealId(orgId)) {
        return <OrgName orgId={orgId} />;
    }
    return <span className="label">-</span>;
}
